import React, { useState } from "react";

import { Edi835Payment, Edi835ProviderAdjustment } from "../types";
import { formatDrgFromFields, drgFieldValue } from "../drgDisplay";
import { formatDisplayDate } from "../../../helpers/dataHelper";
import { CollapsibleSection } from "../CollapsibleSection";
import {
  formatCurrency,
  formatAddress,
  formatEntityName,
  getModifiersFromLine,
  facilityTypeVsPlaceOfServiceVisibility,
} from "../viewerHelpers";
import Field from "./Field";
import TabButton from "./TabButton";
import ExpandCollapseToggle from "./ExpandCollapseToggle";
import TabBarWithArrows from "./TabBarWithArrows";
import PaymentServiceLineRow from "./PaymentServiceLineRow";

const Payment835Viewer: React.FC<{
  payment: Edi835Payment;
  providerAdjustments: Edi835ProviderAdjustment[];
}> = ({ payment, providerAdjustments }) => {
  const [activeTab, setActiveTab] = useState<string>("claim");
  const [allExpanded, setAllExpanded] = useState(true);
  const [serviceLinesExpanded, setServiceLinesExpanded] = useState(false);
  const patient = payment.patient?.person;

  const paymentDrg = formatDrgFromFields(payment.drg, payment.drgCode);

  const hasAnyModifiers =
    payment.serviceLines?.some(
      (line) => getModifiersFromLine(line).length > 0,
    ) ?? false;

  const { showFacilityType, showPlaceOfService, facilityLabel } =
    facilityTypeVsPlaceOfServiceVisibility(
      payment.transaction?.transactionType,
    );

  return (
    <div className="flex flex-col h-full w-full">
      {/* Header */}
      <div className="bg-white px-4 pb-2 pt-1">
        {/* Tabs */}
        <TabBarWithArrows>
          <TabButton
            active={activeTab === "claim"}
            onClick={() => setActiveTab("claim")}
          >
            Payment Details
          </TabButton>
          <TabButton
            active={activeTab === "serviceLines"}
            onClick={() => setActiveTab("serviceLines")}
            count={payment.serviceLines?.length}
            data-tab="serviceLines"
          >
            Service Lines
          </TabButton>
          <TabButton
            active={activeTab === "adjustments"}
            onClick={() => setActiveTab("adjustments")}
            count={payment.adjustments?.length}
            data-tab="adjustments"
          >
            Adjustments
          </TabButton>
          <TabButton
            active={activeTab === "transaction"}
            onClick={() => setActiveTab("transaction")}
          >
            Transaction
          </TabButton>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <ExpandCollapseToggle
              expanded={allExpanded}
              onToggle={(next) => {
                setAllExpanded(next);
                setServiceLinesExpanded(next);
              }}
            />
          </div>
        </TabBarWithArrows>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 scrollbar-thin">
        {activeTab === "claim" && (
          <div>
            <CollapsibleSection title="Key Info" defaultOpen={allExpanded}>
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field
                  label="Patient Ctrl Num"
                  value={payment.patientControlNumber}
                />
                <Field
                  label="Billed Amt"
                  value={formatCurrency(payment.chargeAmount)}
                />
                <Field
                  label="Paid Amt"
                  value={formatCurrency(payment.paymentAmount)}
                />
                <Field
                  label="Patient Responsibility"
                  value={formatCurrency(payment.patientResponsibilityAmount)}
                />
                {showFacilityType && (
                  <Field
                    label={facilityLabel}
                    value={
                      payment.facilityCode
                        ? `${payment.facilityCode.code} - ${payment.facilityCode.desc || ""}`
                        : "-"
                    }
                  />
                )}
                {showPlaceOfService && (
                  <Field
                    label="Place of Service"
                    value={payment.placeOfServiceType || "-"}
                  />
                )}
                <Field label="Claim Status" value={payment.claimStatus} />
                <Field
                  label="Statement Date From"
                  value={formatDisplayDate(payment.statementDateFrom)}
                />
                <Field
                  label="Statement Date To"
                  value={formatDisplayDate(payment.statementDateTo)}
                />
                <Field
                  label="Insurance Plan Type"
                  value={payment.insurancePlanType}
                />
                {paymentDrg && (
                  <Field label="DRG" value={drgFieldValue(paymentDrg)} />
                )}
                {payment.drgWeight != null && (
                  <Field label="DRG Weight" value={String(payment.drgWeight)} />
                )}
                {payment.dischargeFraction != null && (
                  <Field
                    label="Discharge fraction"
                    value={String(payment.dischargeFraction)}
                  />
                )}
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Patient / Subscriber"
              defaultOpen={allExpanded}
              headerRight={
                <span className="text-sm text-gray-500">
                  ID:{" "}
                  <span className="font-medium text-gray-700">
                    {patient?.identifier}
                  </span>
                </span>
              }
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field label="Name" value={formatEntityName(patient)} />
                <Field label="Member ID" value={patient?.identifier} />
                <Field label="Entity Type" value={patient?.entityType} />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Payer"
              defaultOpen={allExpanded}
              headerRight={
                <span className="text-sm text-gray-500">
                  {payment.payer?.lastNameOrOrgName}
                </span>
              }
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field label="PCN" value={payment.payerControlNumber} />
                <Field label="Name" value={payment.payer?.lastNameOrOrgName} />
                <Field label="ID" value={payment.payer?.identifier} />
              </div>
              <div className="mt-2">
                <Field
                  label="Address"
                  value={formatAddress(payment.payer?.address)}
                />
              </div>
            </CollapsibleSection>

            <CollapsibleSection
              title="Payee"
              defaultOpen={allExpanded}
              headerRight={
                <span className="text-sm text-gray-500">
                  {payment.payee?.lastNameOrOrgName}
                </span>
              }
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field label="Name" value={payment.payee?.lastNameOrOrgName} />
                <Field label="NPI" value={payment.payee?.identifier} />
                <Field label="Tax ID" value={payment.payee?.taxId} />
              </div>
            </CollapsibleSection>
          </div>
        )}

        {activeTab === "serviceLines" && (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-sm text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left w-8"></th>
                    <th className="px-3 py-2 text-left">Line</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    {hasAnyModifiers && (
                      <th className="px-3 py-2 text-left">Modifiers</th>
                    )}
                    <th className="px-3 py-2 text-right">Units</th>
                    <th className="px-3 py-2 text-right">Billed</th>
                    <th className="px-3 py-2 text-right">Allowed</th>
                    <th className="px-3 py-2 text-right">Paid</th>
                    <th className="px-3 py-2 text-right">Patient Resp.</th>
                  </tr>
                </thead>
                <tbody>
                  {payment.serviceLines?.map((line, idx) => (
                    <PaymentServiceLineRow
                      key={idx}
                      line={line}
                      index={idx}
                      allExpanded={serviceLinesExpanded}
                      showModifiersColumn={hasAnyModifiers}
                    />
                  )) || (
                    <tr>
                      <td
                        colSpan={8 + (hasAnyModifiers ? 1 : 0)}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No service lines
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-gray-50 border-t border-gray-200">
                  <tr className="font-medium">
                    <td
                      colSpan={4 + (hasAnyModifiers ? 1 : 0)}
                      className="px-3 py-2 text-right text-md"
                    >
                      Total:
                    </td>
                    <td className="px-3 py-2 text-right text-md">
                      {formatCurrency(payment.chargeAmount)}
                    </td>
                    <td className="px-3 py-2 text-right text-md"></td>
                    <td className="px-3 py-2 text-right text-md text-green-600">
                      {formatCurrency(payment.paymentAmount)}
                    </td>
                    <td className="px-3 py-2 text-right text-md">
                      {formatCurrency(payment.patientResponsibilityAmount)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {activeTab === "adjustments" && (
          <div
            id="adjustments-section"
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">
                Claim-Level Adjustments
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-sm text-gray-500 uppercase">
                    <th className="px-3 py-2 text-left">Group</th>
                    <th className="px-3 py-2 text-left">Code</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payment.adjustments?.map((adj, idx) => {
                    const groupCode = adj.group || "";
                    const reasonCode = adj.reason?.code || adj.reasonCode || "";
                    const codeId = `adjustment-${groupCode}-${reasonCode}`;
                    return (
                      <tr
                        key={idx}
                        id={codeId}
                        className="border-b border-gray-100"
                      >
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 rounded text-md font-medium bg-blue-100 text-blue-800">
                            {adj.group}
                          </span>
                        </td>
                        <td className="px-3 py-2 font-mono text-md">
                          {reasonCode}
                        </td>
                        <td className="px-3 py-2 text-md text-gray-600">
                          {adj.reason?.desc || "-"}
                        </td>
                        <td className="px-3 py-2 text-md text-right font-medium text-red-600">
                          {formatCurrency(adj.amount)}
                        </td>
                      </tr>
                    );
                  }) || (
                    <tr>
                      <td
                        colSpan={4}
                        className="px-3 py-4 text-center text-gray-500"
                      >
                        No adjustments
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {providerAdjustments.length > 0 && (
              <div className="mt-4 px-4 py-3 border-t border-gray-200 bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-700">
                  Provider-Level Adjustments
                </h3>
                {providerAdjustments.map((pa, idx) => (
                  <div key={idx} className="mt-2 p-3 bg-white rounded border">
                    <div className="text-sm text-gray-500 mb-2">
                      Provider: {pa.providerIdentifier} | Fiscal Period:{" "}
                      {formatDisplayDate(pa.fiscalPeriodDate)}
                    </div>
                    {pa.adjustments?.map((adj, i) => (
                      <div
                        key={i}
                        className="flex justify-between py-1 text-sm"
                      >
                        <span className="font-mono">{adj.reason?.code}</span>
                        <span className="text-gray-600 flex-1 mx-4">
                          {adj.reason?.desc}
                        </span>
                        <span className="text-red-600">
                          {formatCurrency(adj.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "transaction" && payment.transaction && (
          <div>
            <CollapsibleSection
              title="Transaction Details"
              defaultOpen={allExpanded}
            >
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                }}
              >
                <Field
                  label="Transaction Type"
                  value={payment.transaction.transactionType}
                />
                <Field
                  label="Control Number"
                  value={payment.transaction.controlNumber}
                />
                <Field
                  label="Payment Date"
                  value={formatDisplayDate(payment.transaction.paymentDate)}
                />
                <Field
                  label="Total Payment"
                  value={formatCurrency(payment.transaction.totalPaymentAmount)}
                />
                <Field
                  label="Payment Method"
                  value={payment.transaction.paymentMethodType}
                />
                <Field
                  label="Check/EFT Trace #"
                  value={payment.transaction.checkOrEftTraceNumber}
                />
              </div>
            </CollapsibleSection>
            <CollapsibleSection title="Banking Info" defaultOpen={allExpanded}>
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                }}
              >
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                    Sender
                  </h4>
                  <Field
                    label="Routing #"
                    value={payment.transaction.senderBankRoutingNumber}
                  />
                  <Field
                    label="Account #"
                    value={payment.transaction.senderAccountNumber}
                  />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">
                    Receiver
                  </h4>
                  <Field
                    label="Routing #"
                    value={payment.transaction.receiverBankRoutingNumber}
                  />
                  <Field
                    label="Account #"
                    value={payment.transaction.receiverAccountNumber}
                  />
                </div>
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payment835Viewer;
